import { type VoidFunctionComponent } from 'react';
import { useState } from 'react';
import type Stripe from 'stripe-latest';
import { Modal } from 'react-bootstrap';
import { usePlansContext } from './PlansContext';
import Card from '../../../generic/Card/Card';
import LimitsComponent from '../Limits';
import FeaturesComponent from '../Features';
import styles from './styles.module.less';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../../generic/Button';
import { useUpdatePlan } from '../../../../hooks/subscriptions/useUpdatePlan';
import { FREE_PLAN_NAME } from '../../../../../shared/subscriptions';
import { useSynchronizeSubscription } from '../../../../hooks/subscriptions/useSynchronizeSubscription';
import { useOnMount } from '../../../../hooks';
import Loader from '../../../generic/Loader';
import { useAddToast } from '../../../../contexts/ToastContext';

type MessageModalProps = {
  onHide: () => void;
  planName: string;
};

const MessageModal: VoidFunctionComponent<MessageModalProps> = ({
  onHide,
  planName,
}: MessageModalProps) => {
  const {
    isLoading,
    mutate: synchronizeSubscription,
  } = useSynchronizeSubscription();

  useOnMount(synchronizeSubscription);

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {
            isLoading
              ? 'Updating...'
              : 'Your plan was updated'
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {
          isLoading
            ? <Loader />
            : <p>{`You are now subscribed to plan: '${planName}'.`}</p>
        }
      </Modal.Body>
    </Modal>
  );
};

/**
 * Renders the price component specifically for the FREE plan.
 * Displays information related to the free pricing tier.
 */
const DisplayFreePlan: VoidFunctionComponent = () => {
  const { currentPlan, freePrice: price, getPlanName } = usePlansContext();

  const [
    shouldShowModal,
    setShouldShowModal,
  ] = useState<boolean>(false);

  const {
    isLoading: isUpdating,
    mutateAsync: updatePlan,
  } = useUpdatePlan();

  const addToast = useAddToast();

  if (!currentPlan) {
    return null;
  }

  const isSelectedCurrent = currentPlan.plan === price.id || currentPlan.plan === FREE_PLAN_NAME;

  const onClick = () => {
    updatePlan(price.id)
      .then(() => setShouldShowModal(true))
      .catch((e) => {
        addToast({ type: 'error', content: `An unexpected error happened. Error message: ${e.message}` });
      });
  };

  return (
    <>
      {
        shouldShowModal &&
        <MessageModal
          planName={getPlanName(currentPlan.plan)}
          onHide={() => setShouldShowModal(false)}
        />
      }
      <span className={styles.price}>$0/month</span>
      <Card
        isFullWidth
        padding="xs"
        variant="outline"
      >
        <Card.Content>
          <span className={styles.priceTierDescription}>One size fits all</span>
        </Card.Content>
      </Card>
      <LoaderButton
        size={BUTTON_SIZE.LARGE}
        type={isSelectedCurrent ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.ERROR}
        isFullWidth
        isLoading={isUpdating}
        onClick={onClick}
        isDisabled={isSelectedCurrent}
      >
        { isSelectedCurrent ? 'Current Plan' : 'Downgrade Plan' }
      </LoaderButton>

      <LimitsComponent price={price} />
      <FeaturesComponent product={price.product as Stripe.Product} />
    </>
  );
};

export default DisplayFreePlan;
